import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "./ui/card";
import { Warning, Clock, ClipboardText, Receipt, CurrencyEur, ArrowRight } from "@phosphor-icons/react";
import api from "../lib/api";

const TABS = [
  { key: "scadute", label: "Scadute", tone: "red", icon: Warning },
  { key: "in_scadenza", label: "In scadenza 7gg", tone: "orange", icon: Clock },
  { key: "ferme", label: "Ferme 14gg+", tone: "amber", icon: ClipboardText },
  { key: "preventivi_in_attesa", label: "Preventivi da firmare", tone: "blue", icon: Receipt },
  { key: "fatture_scadute", label: "Fatture insolute", tone: "emerald", icon: CurrencyEur },
];

const TONE_CLASS = {
  red: { badge: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500", tab: "border-red-500 text-red-700" },
  orange: { badge: "bg-orange-50 text-orange-700 border-orange-200", dot: "bg-orange-500", tab: "border-orange-500 text-orange-700" },
  amber: { badge: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500", tab: "border-amber-500 text-amber-700" },
  blue: { badge: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-500", tab: "border-blue-500 text-blue-700" },
  emerald: { badge: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", tab: "border-emerald-500 text-emerald-700" },
};

function Row({ item, tab }) {
  const tone = TONE_CLASS[tab.tone];
  const label = tab.key === "preventivi_in_attesa"
    ? `Preventivo ${item.numero || ""}`.trim()
    : tab.key === "fatture_scadute"
    ? `Avviso ${item.numero || ""}`.trim()
    : (item.titolo || "Incarico");
  const sub = item.cliente_nome || item.descrizione || "—";
  const rightText = tab.key === "in_scadenza"
    ? (item.giorni === 0 ? "Scade oggi" : `Fra ${item.giorni}gg`)
    : tab.key === "fatture_scadute"
    ? `€ ${(item.totale || 0).toFixed(2).replace(".", ",")}`
    : `${item.giorni}gg`;
  return (
    <Link to={item.url} className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors group" data-testid={`radar-row-${tab.key}`}>
      <span className={`h-2 w-2 rounded-full ${tone.dot} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-slate-900 truncate">{label}</div>
        <div className="text-[11px] font-mono text-slate-500 truncate">{sub}</div>
      </div>
      <div className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${tone.badge}`}>{rightText}</div>
      <ArrowRight size={12} className="text-slate-400 group-hover:text-slate-900 flex-shrink-0" />
    </Link>
  );
}

export default function RadarPratiche() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("scadute");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/radar").then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data) return null;

  const totalAll = Object.values(data.totals || {}).reduce((a, b) => a + b, 0);
  if (totalAll === 0) return null; // nothing to worry about, keep dashboard clean

  const activeTab = TABS.find((t) => t.key === active) || TABS[0];
  const items = data[active] || [];

  return (
    <Card className="border-slate-200 shadow-none mb-6 overflow-hidden" data-testid="radar-pratiche">
      <div className="p-5 border-b border-slate-200 flex items-center gap-3">
        <div className="h-8 w-8 rounded-md bg-slate-900 text-white flex items-center justify-center"><Warning size={16} weight="regular" /></div>
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-0.5">Radar Pratiche</div>
          <h3 className="font-heading text-lg font-semibold">Sotto controllo · {totalAll} elementi da guardare</h3>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-slate-200 overflow-x-auto scrollbar-thin">
        {TABS.map((t) => {
          const count = data.totals?.[t.key] || 0;
          const tone = TONE_CLASS[t.tone];
          const isActive = active === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setActive(t.key)}
              data-testid={`radar-tab-${t.key}`}
              disabled={count === 0}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                isActive ? tone.tab : "border-transparent text-slate-500 hover:text-slate-900"
              } ${count === 0 ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <Icon size={14} />
              {t.label}
              <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${count > 0 && isActive ? tone.badge : "bg-slate-100 text-slate-600 border border-slate-200"}`}>{count}</span>
            </button>
          );
        })}
      </div>
      {/* Content */}
      <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto scrollbar-thin">
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">Nessun elemento in questa categoria — ottimo lavoro!</div>
        ) : (
          items.map((it) => <Row key={`${activeTab.key}-${it.id}`} item={it} tab={activeTab} />)
        )}
      </div>
    </Card>
  );
}
